import type { TimesheetAggRow } from '../api/timesheet.api';
import type {
  TimesheetGroupBy,
  TimesheetRow,
  TimesheetCellValue,
  CapacityMap,
} from '../types/timesheet.types';

// ─── Label resolvers ─────────────────────────────────────────────────────────

export interface LabelResolvers {
  resolveUser: (userId: string) => string;
  resolveBoard: (boardId: string) => string;
  resolveItem: (itemId: string) => string;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Transforms flat API rows into a hierarchical TimesheetRow[] tree.
 *
 * @param selectedUserIds - All user IDs from the current filter selection.
 *   Users in this list who have no worklogs will still appear as empty rows.
 *
 * To add a new grouping mode:
 * 1. Add a case to this switch
 * 2. Implement a builder function (or reuse existing ones)
 */
export function buildTimesheetTree(
  aggRows: TimesheetAggRow[],
  groupBy: TimesheetGroupBy,
  resolvers: LabelResolvers,
  capacityMap?: CapacityMap,
  dateColumns?: string[],
  selectedUserIds?: string[],
  subGroupBy?: 'category' | null,
): TimesheetRow[] {
  let tree: TimesheetRow[];
  switch (groupBy) {
    case 'summary':
      tree = buildFlatUserRows(aggRows, resolvers, capacityMap, dateColumns, selectedUserIds);
      break;
    case 'board':
      tree = buildUserChildTree(aggRows, resolvers, 'board', capacityMap, dateColumns, selectedUserIds);
      break;
    case 'item':
      tree = buildUserChildTree(aggRows, resolvers, 'item', capacityMap, dateColumns, selectedUserIds);
      break;
    case 'board_summary':
      tree = buildFlatBoardRows(aggRows, resolvers);
      break;
    case 'board_user':
      tree = buildBoardUserTree(aggRows, resolvers);
      break;
    default:
      tree = buildFlatUserRows(aggRows, resolvers, capacityMap, dateColumns, selectedUserIds);
      break;
  }

  // If sub-grouping by category, inject category children under leaf rows
  if (subGroupBy === 'category') {
    return injectCategorySubRows(tree, aggRows, groupBy);
  }

  return tree;
}

// ─── Category sub-grouping (post-process) ───────────────────────────────────

/**
 * Post-process an existing tree to inject category sub-rows under leaf nodes.
 * For flat modes (summary): adds category children under each user row.
 * For hierarchical modes (board, item): adds category children under each child row.
 */
function injectCategorySubRows(
  tree: TimesheetRow[],
  aggRows: TimesheetAggRow[],
  groupBy: TimesheetGroupBy,
): TimesheetRow[] {
  // Build a lookup: (groupKey, childKey|null) → Map<categoryId, Map<date, hours>>
  const catMap = new Map<string, Map<string, { name: string; color: string; cells: Map<string, number> }>>();

  for (const row of aggRows) {
    const leafKey = row.childKey ? `${row.groupKey}::${row.childKey}` : row.groupKey;
    let catEntries = catMap.get(leafKey);
    if (!catEntries) {
      catEntries = new Map();
      catMap.set(leafKey, catEntries);
    }

    const catId = row.categoryId ?? '__uncategorized__';
    const catName = row.categoryName ?? 'Uncategorized';
    const catColor = row.categoryColor ?? '#808080';
    let catEntry = catEntries.get(catId);
    if (!catEntry) {
      catEntry = { name: catName, color: catColor, cells: new Map() };
      catEntries.set(catId, catEntry);
    }
    catEntry.cells.set(row.date, (catEntry.cells.get(row.date) ?? 0) + row.hours);
  }

  function buildCatChildren(rowKey: string, lookupKey: string, depth: number): TimesheetRow[] {
    const cats = catMap.get(lookupKey);
    if (!cats || cats.size === 0) return [];

    const children: TimesheetRow[] = [];
    for (const [catId, catData] of cats) {
      const cells: Record<string, TimesheetCellValue> = {};
      let totalActual = 0;
      for (const [date, hours] of catData.cells) {
        cells[date] = { actual: hours, planned: null, billableHours: null };
        totalActual += hours;
      }
      children.push({
        rowKey: `${rowKey}::cat::${catId}`,
        label: catData.name,
        entityId: catId,
        entityType: 'item',
        depth,
        expandable: false,
        cells,
        totalActual: Math.round(totalActual * 100) / 100,
        totalPlanned: null,
        progress: null,
        avatarUrl: null,
      });
    }
    return children;
  }

  const isHierarchical = groupBy === 'board' || groupBy === 'item' || groupBy === 'board_user';

  return tree.map((parentRow) => {
    if (isHierarchical && parentRow.children && parentRow.children.length > 0) {
      // Inject category children under each existing child row
      const newChildren = parentRow.children.map((childRow) => {
        const lookupKey = `${parentRow.entityId}::${childRow.entityId}`;
        const catChildren = buildCatChildren(childRow.rowKey, lookupKey, childRow.depth + 1);
        if (catChildren.length === 0) return childRow;
        return {
          ...childRow,
          expandable: true,
          children: catChildren,
          countBadge: `${catChildren.length} categories`,
        };
      });
      return { ...parentRow, children: newChildren };
    } else {
      // Flat mode: inject category children directly under the primary row
      const lookupKey = parentRow.entityId;
      const catChildren = buildCatChildren(parentRow.rowKey, lookupKey, parentRow.depth + 1);
      if (catChildren.length === 0) return parentRow;
      return {
        ...parentRow,
        expandable: true,
        children: [
          ...(parentRow.children ?? []),
          ...catChildren,
        ],
        countBadge: `${catChildren.length} categories`,
      };
    }
  });
}

// ─── Flat user rows (Summary mode) ──────────────────────────────────────────

function buildFlatUserRows(
  aggRows: TimesheetAggRow[],
  resolvers: LabelResolvers,
  capacityMap?: CapacityMap,
  dateColumns?: string[],
  selectedUserIds?: string[],
): TimesheetRow[] {
  // Group by user
  const userMap = new Map<string, Map<string, TimesheetCellValue>>();

  for (const row of aggRows) {
    let cells = userMap.get(row.groupKey);
    if (!cells) {
      cells = new Map();
      userMap.set(row.groupKey, cells);
    }

    const existing = cells.get(row.date);
    if (existing) {
      existing.actual += row.hours;
      if (existing.billableHours != null) existing.billableHours += row.billableHours ?? 0;
    } else {
      cells.set(row.date, { actual: row.hours, planned: null, billableHours: row.billableHours ?? 0 });
    }
  }

  // Ensure all selected users appear, even those with no worklogs
  if (selectedUserIds) {
    for (const userId of selectedUserIds) {
      if (!userMap.has(userId)) {
        userMap.set(userId, new Map());
      }
    }
  }

  // Pre-compute child counts in a single pass
  const childCounts = buildChildCountMap(aggRows);

  const result: TimesheetRow[] = [];
  for (const [userId, cellsMap] of userMap) {
    const cells: Record<string, TimesheetCellValue> = {};
    let totalActual = 0;

    for (const [date, value] of cellsMap) {
      cells[date] = value;
      totalActual += value.actual;
    }

    const itemCount = childCounts.get(userId) ?? 0;

    result.push({
      rowKey: `user-${userId}`,
      label: resolvers.resolveUser(userId),
      countBadge: itemCount > 0 ? `${itemCount} items` : undefined,
      entityId: userId,
      entityType: 'user',
      depth: 0,
      expandable: false,
      cells,
      totalActual: round(totalActual),
      totalPlanned: null,
      progress: computeProgress(userId, totalActual, capacityMap, dateColumns),
    });
  }

  // Sort by name
  result.sort((a, b) => a.label.localeCompare(b.label));
  return result;
}

// ─── User -> Child tree (by Board or by Item) ──────────────────────────────

function buildUserChildTree(
  aggRows: TimesheetAggRow[],
  resolvers: LabelResolvers,
  childType: 'board' | 'item',
  capacityMap?: CapacityMap,
  dateColumns?: string[],
  selectedUserIds?: string[],
): TimesheetRow[] {
  // Group: userId -> childId -> date -> hours
  const tree = new Map<
    string,
    Map<string, Map<string, TimesheetCellValue>>
  >();

  for (const row of aggRows) {
    const userId = row.groupKey;
    const childId = row.childKey ?? 'unknown';

    let userChildren = tree.get(userId);
    if (!userChildren) {
      userChildren = new Map();
      tree.set(userId, userChildren);
    }

    let childCells = userChildren.get(childId);
    if (!childCells) {
      childCells = new Map();
      userChildren.set(childId, childCells);
    }

    const existing = childCells.get(row.date);
    if (existing) {
      existing.actual += row.hours;
      if (existing.billableHours != null) existing.billableHours += row.billableHours ?? 0;
    } else {
      childCells.set(row.date, { actual: row.hours, planned: null, billableHours: row.billableHours ?? 0 });
    }
  }

  // Ensure all selected users appear, even those with no worklogs
  if (selectedUserIds) {
    for (const userId of selectedUserIds) {
      if (!tree.has(userId)) {
        tree.set(userId, new Map());
      }
    }
  }

  const result: TimesheetRow[] = [];
  const resolveChild =
    childType === 'board' ? resolvers.resolveBoard : resolvers.resolveItem;
  const childEntityType = childType;
  const childLabel = childType === 'board' ? 'boards' : 'items';

  for (const [userId, childrenMap] of tree) {
    // Build parent row (aggregated across all children)
    const parentCells: Record<string, TimesheetCellValue> = {};
    let parentTotal = 0;
    const children: TimesheetRow[] = [];

    for (const [childId, cellsMap] of childrenMap) {
      const childCells: Record<string, TimesheetCellValue> = {};
      let childTotal = 0;

      for (const [date, value] of cellsMap) {
        childCells[date] = value;
        childTotal += value.actual;

        // Aggregate into parent
        if (parentCells[date]) {
          parentCells[date].actual += value.actual;
          if (parentCells[date].billableHours != null) parentCells[date].billableHours! += value.billableHours ?? 0;
        } else {
          parentCells[date] = { actual: value.actual, planned: null, billableHours: value.billableHours ?? 0 };
        }
      }
      parentTotal += childTotal;

      children.push({
        rowKey: `user-${userId}-${childType}-${childId}`,
        label: resolveChild(childId),
        entityId: childId,
        entityType: childEntityType,
        depth: 1,
        expandable: false,
        cells: childCells,
        totalActual: round(childTotal),
        totalPlanned: null,
        progress: null, // Progress not applicable for board/item rows
      });
    }

    // Sort children by total hours desc
    children.sort((a, b) => b.totalActual - a.totalActual);

    result.push({
      rowKey: `user-${userId}`,
      label: resolvers.resolveUser(userId),
      countBadge: children.length > 0 ? `${children.length} ${childLabel}` : undefined,
      entityId: userId,
      entityType: 'user',
      depth: 0,
      expandable: children.length > 0,
      cells: parentCells,
      totalActual: round(parentTotal),
      totalPlanned: null,
      progress: computeProgress(userId, parentTotal, capacityMap, dateColumns),
      children,
    });
  }

  // Sort parents by name
  result.sort((a, b) => a.label.localeCompare(b.label));
  return result;
}

// ─── Flat board rows (Board Summary mode) ───────────────────────────────────

function buildFlatBoardRows(
  aggRows: TimesheetAggRow[],
  resolvers: LabelResolvers,
): TimesheetRow[] {
  // Group by board (group_key = board_id in board_summary mode)
  const boardMap = new Map<string, Map<string, TimesheetCellValue>>();

  for (const row of aggRows) {
    let cells = boardMap.get(row.groupKey);
    if (!cells) {
      cells = new Map();
      boardMap.set(row.groupKey, cells);
    }

    const existing = cells.get(row.date);
    if (existing) {
      existing.actual += row.hours;
      if (existing.billableHours != null) existing.billableHours += row.billableHours ?? 0;
    } else {
      cells.set(row.date, { actual: row.hours, planned: null, billableHours: row.billableHours ?? 0 });
    }
  }

  // Pre-compute child counts in a single pass
  const childCounts = buildChildCountMap(aggRows);

  const result: TimesheetRow[] = [];
  for (const [boardId, cellsMap] of boardMap) {
    const cells: Record<string, TimesheetCellValue> = {};
    let totalActual = 0;

    for (const [date, value] of cellsMap) {
      cells[date] = value;
      totalActual += value.actual;
    }

    const userCount = childCounts.get(boardId) ?? 0;

    result.push({
      rowKey: `board-${boardId}`,
      label: resolvers.resolveBoard(boardId),
      countBadge: userCount > 0 ? `${userCount} users` : undefined,
      entityId: boardId,
      entityType: 'board',
      depth: 0,
      expandable: false,
      cells,
      totalActual: round(totalActual),
      totalPlanned: null,
      progress: null, // No capacity concept for boards
    });
  }

  // Sort by name
  result.sort((a, b) => a.label.localeCompare(b.label));
  return result;
}

// ─── Board -> User tree (Board by User mode) ────────────────────────────────

function buildBoardUserTree(
  aggRows: TimesheetAggRow[],
  resolvers: LabelResolvers,
): TimesheetRow[] {
  // Group: boardId -> userId -> date -> hours
  const tree = new Map<
    string,
    Map<string, Map<string, TimesheetCellValue>>
  >();

  for (const row of aggRows) {
    const boardId = row.groupKey;
    const userId = row.childKey ?? 'unknown';

    let boardChildren = tree.get(boardId);
    if (!boardChildren) {
      boardChildren = new Map();
      tree.set(boardId, boardChildren);
    }

    let userCells = boardChildren.get(userId);
    if (!userCells) {
      userCells = new Map();
      boardChildren.set(userId, userCells);
    }

    const existing = userCells.get(row.date);
    if (existing) {
      existing.actual += row.hours;
      if (existing.billableHours != null) existing.billableHours += row.billableHours ?? 0;
    } else {
      userCells.set(row.date, { actual: row.hours, planned: null, billableHours: row.billableHours ?? 0 });
    }
  }

  const result: TimesheetRow[] = [];

  for (const [boardId, usersMap] of tree) {
    // Build parent board row (aggregated across all users)
    const parentCells: Record<string, TimesheetCellValue> = {};
    let parentTotal = 0;
    const children: TimesheetRow[] = [];

    for (const [userId, cellsMap] of usersMap) {
      const childCells: Record<string, TimesheetCellValue> = {};
      let childTotal = 0;

      for (const [date, value] of cellsMap) {
        childCells[date] = value;
        childTotal += value.actual;

        // Aggregate into parent
        if (parentCells[date]) {
          parentCells[date].actual += value.actual;
          if (parentCells[date].billableHours != null) parentCells[date].billableHours! += value.billableHours ?? 0;
        } else {
          parentCells[date] = { actual: value.actual, planned: null, billableHours: value.billableHours ?? 0 };
        }
      }
      parentTotal += childTotal;

      children.push({
        rowKey: `board-${boardId}-user-${userId}`,
        label: resolvers.resolveUser(userId),
        entityId: userId,
        entityType: 'user',
        depth: 1,
        expandable: false,
        cells: childCells,
        totalActual: round(childTotal),
        totalPlanned: null,
        progress: null, // Progress not shown in board timesheets (partial hours per board)
      });
    }

    // Sort children by total hours desc
    children.sort((a, b) => b.totalActual - a.totalActual);

    result.push({
      rowKey: `board-${boardId}`,
      label: resolvers.resolveBoard(boardId),
      countBadge: children.length > 0 ? `${children.length} users` : undefined,
      entityId: boardId,
      entityType: 'board',
      depth: 0,
      expandable: children.length > 0,
      cells: parentCells,
      totalActual: round(parentTotal),
      totalPlanned: null,
      progress: null, // No capacity concept for board-level rows
      children,
    });
  }

  // Sort parents by name
  result.sort((a, b) => a.label.localeCompare(b.label));
  return result;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Pre-compute child count per groupKey in a single O(n) pass.
 * Returns a Map: groupKey -> number of distinct childKeys.
 */
function buildChildCountMap(
  aggRows: TimesheetAggRow[],
): Map<string, number> {
  const childSets = new Map<string, Set<string>>();
  for (const row of aggRows) {
    if (row.childKey) {
      let set = childSets.get(row.groupKey);
      if (!set) {
        set = new Set();
        childSets.set(row.groupKey, set);
      }
      set.add(row.childKey);
    }
  }
  const result = new Map<string, number>();
  for (const [key, set] of childSets) {
    result.set(key, set.size);
  }
  return result;
}

function computeProgress(
  userId: string,
  totalActual: number,
  capacityMap?: CapacityMap,
  dateColumns?: string[],
): number | null {
  if (!capacityMap || !dateColumns) return null;
  const userCap = capacityMap[userId];
  if (!userCap) return null;

  let totalCapacity = 0;
  for (const date of dateColumns) {
    const cap = userCap[date];
    if (cap) {
      totalCapacity += cap.availableHours;
    }
  }

  if (totalCapacity === 0) return null;
  return round((totalActual / totalCapacity) * 100);
}

function round(val: number): number {
  return Math.round(val * 100) / 100;
}

// ─── Extract user IDs from tree ──────────────────────────────────────────────

/**
 * Extract all unique user IDs from the tree for capacity fetching.
 */
export function extractUserIds(rows: TimesheetRow[]): string[] {
  const ids = new Set<string>();
  for (const row of rows) {
    if (row.entityType === 'user') {
      ids.add(row.entityId);
    }
    if (row.children) {
      for (const child of row.children) {
        if (child.entityType === 'user') {
          ids.add(child.entityId);
        }
      }
    }
  }
  return Array.from(ids);
}
